'use client'

import { motion } from 'framer-motion'
import {
  CalendarClockIcon,
  CalendarDaysIcon,
  CoinsIcon,
  LandmarkIcon,
  LockOpenIcon,
  type LucideIcon,
  TimerIcon,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'

import { Skeleton } from '@/components/ui/skeleton'
import { normalizeFedwatchGrid } from '@/features/data/components/widgets/content/fedwatch/fedwatch-normalize'
import { HOME_LIST, HOME_ROW, HomePanel } from '@/features/home/components/home-motion'
import type { HomeDailyBrief } from '@/features/home/hooks/use-home-daily-brief'
import { OPTION_ASSETS } from '@/features/home/lib/home-datapoints'
import {
  buildUpcoming,
  type UpcomingEvent,
  type UpcomingKind,
} from '@/features/home/lib/home-upcoming'
import { useNow } from '@/hooks/use-now'
import { useNumberFormat } from '@/hooks/use-number-format'
import { nextFundingDate } from '@/lib/next-funding'
import { cn } from '@/lib/utils'
import { useCoinMarketCalEventsInfinite } from '@/service/coinmarketcal.hook'
import { useEconomicCalendarHistory } from '@/service/economic-calendar.hook'
import { useFedwatchGrid } from '@/service/fedwatch.hook'
import { useMaxPain } from '@/service/options-analytics.hook'
import { useTokenUnlocksInfinite } from '@/service/tokenomist.hook'
import { eventPrecisionIsApproximate } from '@/service/tokenomist.types'
import { Exchange } from '@/types/generated/common/types'
import { EventVolatility } from '@/types/generated/public_api'

const DAY_MS = 86_400_000
const HORIZON_MS = 8 * DAY_MS

const KIND_ICON: Record<UpcomingKind, LucideIcon> = {
  expiry: CalendarClockIcon,
  weeklyExpiry: CalendarClockIcon,
  funding: CoinsIcon,
  unlock: LockOpenIcon,
  event: CalendarDaysIcon,
  fomc: LandmarkIcon,
  macro: TimerIcon,
}

const KIND_TONE: Record<UpcomingKind, string> = {
  expiry: 'bg-ox-gold/10 text-ox-gold',
  weeklyExpiry: 'bg-shell-accent/10 text-shell-accent',
  funding: 'bg-foreground/5 text-foreground/60',
  unlock: 'bg-ox-cyan/10 text-ox-cyan',
  event: 'bg-shell-accent/10 text-shell-accent',
  fomc: 'bg-chart-red/10 text-chart-red',
  macro: 'bg-foreground/5 text-foreground/60',
}

function useMaxPainFor(asset: string | undefined) {
  const { data } = useMaxPain(
    { exchange: Exchange.EXCHANGE_DERIBIT, underlying: asset ?? '' },
    Boolean(asset)
  )
  const point = data?.points.find((p) => Number(p.expiryMs) > Date.now())
  return point ? Number(point.maxPainStrike) : undefined
}

/**
 * Coming up for your positions: what settles or prints in the next week that touches what the
 * user holds — their expiries, the next funding on each perp, the weekly options expiry on their
 * coins (with max pain), token unlocks and project events on their coins, the Fed and
 * high-impact releases — each with a countdown.
 */
export function HomeUpcoming({ brief, className }: { brief: HomeDailyBrief; className?: string }) {
  const t = useTranslations('trade.home.upcoming')
  const fmt = useNumberFormat()
  const now = useNow(30_000)
  const [range] = useState(() => {
    const from = Date.now()
    return { from: String(from), to: String(from + HORIZON_MS) }
  })

  // Max pain for (at most) the two biggest option-listed coins held.
  const optionCoins = brief.exposures
    .filter((exposure) => exposure.positions.length > 0 && OPTION_ASSETS.has(exposure.asset))
    .map((exposure) => exposure.asset)
  const painA = useMaxPainFor(optionCoins[0])
  const painB = useMaxPainFor(optionCoins[1])

  const { data: fedData } = useFedwatchGrid({ enableStream: false })
  const { data: calendar, isPending } = useEconomicCalendarHistory({ ...range, limit: 100 })
  // Unlocks and project events across the market for the week; the user's coins are picked out.
  const holdsCoins = brief.exposures.some((exposure) => exposure.positions.length > 0)
  const { data: unlockPages } = useTokenUnlocksInfinite({ ...range, limit: 100 }, holdsCoins)
  const { data: eventPages } = useCoinMarketCalEventsInfinite({ ...range, limit: 100 }, holdsCoins)

  const events = useMemo(() => {
    const grid = normalizeFedwatchGrid(fedData)
    const meeting = grid.rows.find((row) => row.meetingMs >= now - DAY_MS)
    const move =
      meeting?.modeBp != null && grid.standingLowerBp !== null
        ? meeting.modeBp - grid.standingLowerBp
        : null
    const nextFunding: Record<string, number> = {}
    for (const exposure of brief.exposures) {
      for (const perp of exposure.perps) {
        const at = nextFundingDate(brief.tickerOf(perp.symbol)?.nextFundingTime)
        if (at) nextFunding[perp.symbol] = at.getTime()
      }
    }
    const maxPain: Record<string, number> = {}
    if (optionCoins[0] && painA) maxPain[optionCoins[0]] = painA
    if (optionCoins[1] && painB) maxPain[optionCoins[1]] = painB

    return buildUpcoming({
      exposures: brief.exposures,
      now,
      nextFunding,
      maxPain,
      optionAssets: OPTION_ASSETS,
      fomc: meeting
        ? {
            at: meeting.meetingMs + 18 * 3600_000,
            outcome: move === null ? '' : move === 0 ? 'hold' : move < 0 ? 'cut' : 'hike',
          }
        : null,
      macro: (calendar?.events ?? [])
        .filter((event) => (event.volatility ?? 0) >= EventVolatility.EVENT_VOLATILITY_HIGH)
        .map((event) => ({
          id: event.eventDateId,
          at: Number(event.dateUtc),
          name: event.name,
          currency: event.currencyCode || event.countryCode,
          impact: 3,
        })),
      unlocks: (unlockPages?.pages ?? [])
        .flatMap((page) => page.events)
        .map((unlock) => ({
          id: `${unlock.tokenId}:${unlock.unlockDate}`,
          at: Number(unlock.unlockDate),
          asset: unlock.tokenSymbol.toUpperCase(),
          valueUsd: unlock.totalCliffValue,
          pctOfMcap: unlock.valueToMarketCap ?? null,
          approximate: eventPrecisionIsApproximate(unlock, now),
        })),
      projectEvents: (eventPages?.pages ?? [])
        .flatMap((page) => page.events)
        .filter((event) => !event.cancelledAt)
        .map((event) => ({
          id: event.eventId,
          at: Number(event.eventDate),
          assets: event.coinSymbols,
          title: event.title,
          estimated: event.isEstimated,
        })),
      horizonMs: HORIZON_MS,
      limit: 8,
    })
    // optionCoins is derived from brief.exposures each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brief, fedData, calendar, unlockPages, eventPages, painA, painB, now])

  const countdown = (at: number) => {
    const ms = at - now
    if (ms < 3600_000) return t('inMinutes', { count: Math.max(1, Math.round(ms / 60_000)) })
    if (ms < DAY_MS) return t('inHours', { count: Math.round(ms / 3600_000) })
    return t('inDays', { count: Math.round(ms / DAY_MS) })
  }

  const label = (event: UpcomingEvent) => {
    switch (event.kind) {
      case 'expiry':
        return t(event.params?.type === 'option' ? 'optionExpiry' : 'futureExpiry', {
          symbol: event.subject,
        })
      case 'weeklyExpiry':
        return event.params?.maxPain
          ? t('weeklyExpiryPain', {
              asset: event.subject,
              pain: fmt.number(Number(event.params.maxPain), { maximumFractionDigits: 0 }),
            })
          : t('weeklyExpiry', { asset: event.subject })
      case 'funding':
        return t('funding', { symbol: event.subject })
      case 'unlock': {
        const value = fmt.number(Number(event.params?.value ?? 0), {
          style: 'currency',
          currency: 'USD',
          notation: 'compact',
          maximumFractionDigits: 1,
        })
        return event.params?.pct !== undefined
          ? t('unlockPct', {
              asset: event.subject,
              value,
              pct: fmt.number(Number(event.params.pct), { maximumFractionDigits: 2 }),
            })
          : t('unlock', { asset: event.subject, value })
      }
      case 'event':
        return t(event.params?.estimated === 'yes' ? 'eventEstimated' : 'event', {
          asset: event.subject,
          title: String(event.params?.title ?? ''),
        })
      case 'fomc':
        return event.params?.outcome
          ? t('fomc', { outcome: t(`outcomes.${event.params.outcome}`) })
          : t('fomcPlain')
      case 'macro':
        return `${event.subject} · ${event.params?.name ?? ''}`
    }
  }

  return (
    <HomePanel title={t('title')} className={className}>
      {isPending && events.length === 0 ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-9 w-full" />
          ))}
        </div>
      ) : events.length === 0 ? (
        <p className="text-foreground/50 py-6 text-sm">{t('empty')}</p>
      ) : (
        // A timeline: a rail down the left, one stop per event, soonest on top.
        <motion.ol
          variants={HOME_LIST}
          initial="hidden"
          animate="show"
          className="relative flex flex-col"
        >
          <span
            className="bg-foreground/10 absolute top-3 bottom-3 left-[13px] w-px"
            aria-hidden="true"
          />
          {events.map((event) => {
            const Icon = KIND_ICON[event.kind]
            const soon = event.at - now < 6 * 3600_000
            return (
              <motion.li
                key={event.id}
                variants={HOME_ROW}
                className="relative flex items-center gap-3 py-2"
              >
                <span
                  className={cn(
                    'relative z-10 flex size-7 shrink-0 items-center justify-center rounded-full',
                    KIND_TONE[event.kind]
                  )}
                >
                  <Icon className="size-3.5" />
                </span>
                <span className="text-foreground min-w-0 flex-1 truncate text-sm tracking-tight">
                  {label(event)}
                </span>
                <span
                  className={cn(
                    'shrink-0 text-xs whitespace-nowrap',
                    soon ? 'text-ox-gold' : 'text-foreground/45'
                  )}
                >
                  {countdown(event.at)}
                </span>
              </motion.li>
            )
          })}
        </motion.ol>
      )}
    </HomePanel>
  )
}
